import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, UserPlus } from "lucide-react";
import { adminApi } from "../../lib/api";
import { useSettings } from "../../lib/settings";
import { ModalShell } from "./AssignModals";

const inputCls = "w-full px-3 py-2 text-sm bg-[#0A0A0A] border border-[#262626] focus:outline-none focus:border-blood";
const Label = ({ children }) => <label className="block text-xs uppercase tracking-wider text-[#8a857d] mb-1.5">{children}</label>;

const Check = ({ checked, onChange, testid, children }) => (
  <label className="flex items-start gap-2.5 cursor-pointer" data-testid={testid}>
    <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="accent-blood mt-0.5" />
    <span className="text-sm text-[#F2EBE1]">{children}</span>
  </label>
);

const DatePicker = ({ dates, selected, toggle, setAll }) => {
  const { formatDate } = useSettings();
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <Label>Available dates ({selected.size})</Label>
        <div className="flex gap-3 text-[10px] uppercase tracking-wider">
          <button type="button" onClick={() => setAll(true)} data-testid="add-cast-dates-all" className="text-[#A19D94] hover:text-blood">All</button>
          <button type="button" onClick={() => setAll(false)} data-testid="add-cast-dates-none" className="text-[#A19D94] hover:text-blood">None</button>
        </div>
      </div>
      {dates.length === 0 ? <p className="text-xs text-[#6B6862]">No show dates set up yet.</p> : (
        <div className="flex flex-wrap gap-1.5" data-testid="add-cast-dates">
          {dates.map((d) => {
            const on = selected.has(d.id);
            return (
              <button type="button" key={d.id} onClick={() => toggle(d.id)} data-testid={`add-cast-date-${d.id}`}
                className={`px-2.5 py-1 text-[11px] border transition-colors ${on ? "border-blood bg-blood/15 text-[#F2EBE1]" : "border-[#262626] text-[#8a857d] hover:border-[#3a3835]"}`}>
                {d.name || formatDate(d.date)}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export function AddCastModal({ onClose, onAdded }) {
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", role_type_id: "", personal_message: "" });
  const [dates, setDates] = useState([]);
  const [roles, setRoles] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [fillRota, setFillRota] = useState(true);
  const [sendContract, setSendContract] = useState(true);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    adminApi.get("/admin/show-dates").then(({ data }) => setDates(data.items || []));
    adminApi.get("/admin/roles").then(({ data }) => setRoles(data.items || []));
  }, []);

  const roleTypes = useMemo(() => {
    const m = new Map();
    roles.forEach((r) => {
      const k = r.role_type_id || r.id;
      if (!m.has(k)) m.set(k, { id: k, name: r.base_name || r.name, count: 0 });
      m.get(k).count += 1;
    });
    return [...m.values()];
  }, [roles]);

  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const setAll = (on) => setSelected(on ? new Set(dates.map((d) => d.id)) : new Set());
  const roleName = roleTypes.find((r) => r.id === form.role_type_id)?.name;
  const missing = [
    !form.first_name.trim() && "a first name",
    !form.email.trim() && "an email",
    sendContract && !form.role_type_id && "a role (needed to send the contract)",
  ].filter(Boolean);
  const [tried, setTried] = useState(false);
  const needRole = tried && sendContract && !form.role_type_id;

  const save = async () => {
    setTried(true);
    if (missing.length) { toast.error(`Please add ${missing.join(", ")}.`); return; }
    setSaving(true);
    try {
      const { data } = await adminApi.post("/admin/cast/manual", {
        ...form, role_type_id: form.role_type_id || null, available_dates: [...selected],
        fill_rota: !!form.role_type_id && fillRota, send_contract: sendContract,
      });
      const bits = [data.assigned_nights ? `on the rota for ${data.assigned_nights} night(s)` : null, data.contract_sent ? "contract emailed" : null].filter(Boolean);
      toast.success(`${data.full_name} added to the cast${bits.length ? ` — ${bits.join(", ")}` : ""}`);
      onAdded();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't add cast member.");
    } finally { setSaving(false); }
  };

  return (
    <ModalShell testid="add-cast-modal" title="Add Cast Member" subtitle="For people joining without an application or audition" onClose={onClose}
      footer={<>
        <button onClick={onClose} data-testid="add-cast-cancel" className="px-4 py-2 text-xs uppercase tracking-wider border border-[#262626] text-[#A19D94] hover:text-[#F2EBE1]">Cancel</button>
        <button onClick={save} disabled={saving} data-testid="add-cast-save"
          className="inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider bg-blood text-white hover:bg-[#FF3333] disabled:opacity-40 disabled:cursor-not-allowed">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <UserPlus size={14} />} {sendContract ? "Add & Send Contract" : "Add to Cast"}
        </button>
      </>}>
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div><Label>First name *</Label><input value={form.first_name} onChange={set("first_name")} data-testid="add-cast-first-name" className={inputCls} /></div>
          <div><Label>Last name</Label><input value={form.last_name} onChange={set("last_name")} data-testid="add-cast-last-name" className={inputCls} /></div>
          <div><Label>Email *</Label><input type="email" value={form.email} onChange={set("email")} data-testid="add-cast-email" className={inputCls} /></div>
          <div><Label>Phone</Label><input value={form.phone} onChange={set("phone")} data-testid="add-cast-phone" className={inputCls} /></div>
        </div>
        <DatePicker dates={dates} selected={selected} toggle={toggle} setAll={setAll} />
        <div>
          <Label>Role {sendContract && "*"}</Label>
          <select value={form.role_type_id} onChange={set("role_type_id")} data-testid="add-cast-role" className={`${inputCls} ${needRole ? "!border-blood" : ""}`}>
            <option value="">No role yet</option>
            {roleTypes.map((r) => <option key={r.id} value={r.id}>{r.name}{r.count > 1 ? ` (${r.count} positions)` : ""}</option>)}
          </select>
          {sendContract && !form.role_type_id && (
            <p className={`text-[11px] mt-1.5 ${needRole ? "text-blood" : "text-[#6B6862]"}`} data-testid="add-cast-role-hint">
              Pick a role to send the contract — or untick "Email them the role offer" below to add them with no role yet.
            </p>
          )}
        </div>
        {form.role_type_id && (
          <Check checked={fillRota} onChange={setFillRota} testid="add-cast-fill-rota">
            Put them on the rota as <span className="text-blood font-medium">{roleName}</span> for every available night where a position is free (you can adjust on the Roster).
          </Check>
        )}
        <Check checked={sendContract} onChange={setSendContract} testid="add-cast-send-contract">
          Email them the role offer &amp; contract to accept and sign now.
        </Check>
        {sendContract && (
          <div>
            <Label>Personal message (optional)</Label>
            <textarea value={form.personal_message} onChange={set("personal_message")} rows={3} data-testid="add-cast-message"
              placeholder="Add a personal note…" className={`${inputCls} resize-none`} />
          </div>
        )}
      </div>
    </ModalShell>
  );
}
